import React, { useMemo } from 'react';
import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material';
import {
  CreateNewFolder as CreateNewFolderIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';

export interface DestinationFolderSelectorProps {
  /** Current directory path (default selection) */
  currentPath: string;

  /** Array of existing folder names for autocomplete */
  existingFolders: string[];

  /** Selected/typed destination path */
  value: string;

  /** Callback when path changes */
  onChange: (path: string) => void;

  /** Error message for validation */
  error?: string;

  /** Whether the selector is disabled */
  disabled?: boolean;
}

/**
 * DestinationFolderSelector Component
 *
 * Folder path selector with autocomplete for existing folders and free-text
 * input for new paths. The input always shows the raw path value — empty
 * string means root, which is indicated by the placeholder text.
 *
 * **Validates: Requirements 3.3.5, 3.4.3**
 */
const DestinationFolderSelector: React.FC<DestinationFolderSelectorProps> = ({
  currentPath,
  existingFolders,
  value,
  onChange,
  error,
  disabled = false,
}) => {
  // Build dropdown options: current path + subfolders as full paths
  const options = useMemo(() => {
    const set = new Set<string>();

    // Always include current path (empty string = root)
    set.add(currentPath);

    // Add subfolders as full paths
    existingFolders.forEach(folder => {
      const fullPath = currentPath ? `${currentPath}${folder}/` : `${folder}/`;
      set.add(fullPath);
    });

    return Array.from(set);
  }, [currentPath, existingFolders]);

  const isKnownFolder = (path: string): boolean => options.includes(path);

  return (
    <Box>
      <Autocomplete
        freeSolo
        options={options}
        value={value}
        onChange={(_event, newValue) => onChange(newValue ?? '')}
        onInputChange={(_event, newInput, reason) => {
          // "input" = user typing; "reset" = Autocomplete syncing — ignore resets
          if (reason === 'input') onChange(newInput);
        }}
        getOptionLabel={option => option}
        filterOptions={(opts, state) => {
          // Always show all options; filter only when user has typed something
          if (!state.inputValue) return opts;
          const lower = state.inputValue.toLowerCase();
          return opts.filter(o => o.toLowerCase().includes(lower));
        }}
        disabled={disabled}
        renderInput={params => (
          <TextField
            {...params}
            label="Destination Folder"
            placeholder="Root (leave empty) or type a path"
            error={!!error}
            helperText={
              error ||
              'Select a folder or type a new path (e.g., reports/2025/)'
            }
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                  <FolderIcon color="action" fontSize="small" />
                </Box>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          const existing = isKnownFolder(option);
          const isCurrent = option === currentPath;
          const label = option === '' ? '/ (Root)' : option;

          return (
            <li {...props} key={option || '__root__'}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                }}
              >
                {existing ? (
                  <FolderIcon fontSize="small" color="action" />
                ) : (
                  <CreateNewFolderIcon fontSize="small" color="primary" />
                )}
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                  {label}
                </Typography>
                {isCurrent && (
                  <Chip label="Current" size="small" color="primary" />
                )}
                {!existing && option !== '' && (
                  <Chip label="New" size="small" color="success" />
                )}
              </Box>
            </li>
          );
        }}
        sx={{ width: '100%' }}
      />
    </Box>
  );
};

export default DestinationFolderSelector;
